// The password rule for sign-up, reset and change — the same rule the server enforces
// (supabase/functions/account-auth/password.ts; keep the two in step) — and the strength meter under the field.
//
//   Rules:  8–128 characters, at least one letter, at least one special character (not a letter or digit).
//   Weak    the rules aren't met (the form can't be sent)
//   Medium  the rules are met
//   Strong  the rules are met, 12+ characters, and at least three of: lowercase, uppercase, digit, special

export const MIN_PASSWORD = 8;
export const MAX_PASSWORD = 128;

const LETTER = /\p{L}/u;
const SPECIAL = /[^\p{L}\p{N}]/u;

export type Strength = "weak" | "medium" | "strong";

export interface Rule {
  id: "length" | "letter" | "special";
  label: string;
  ok: boolean;
}

/** The checklist under the field, in order. */
export function passwordRules(password: string): Rule[] {
  return [
    { id: "length", label: `${MIN_PASSWORD}–${MAX_PASSWORD} characters`, ok: password.length >= MIN_PASSWORD && password.length <= MAX_PASSWORD },
    { id: "letter", label: "At least one letter", ok: LETTER.test(password) },
    { id: "special", label: "At least one special character (like ! ? # or -)", ok: SPECIAL.test(password) },
  ];
}

/** The first rule broken, as the server words it; null when the password meets them all. */
export function passwordProblem(password: string): string | null {
  if (password.length < MIN_PASSWORD) return `Use at least ${MIN_PASSWORD} characters.`;
  if (password.length > MAX_PASSWORD) return `Use at most ${MAX_PASSWORD} characters.`;
  if (!LETTER.test(password)) return "Include at least one letter.";
  if (!SPECIAL.test(password)) return "Include at least one special character, like ! ? # or -.";
  return null;
}

export function passwordStrength(password: string): Strength {
  if (passwordProblem(password)) return "weak";
  const kinds = [/\p{Ll}/u, /\p{Lu}/u, /\p{N}/u, SPECIAL].filter((r) => r.test(password)).length;
  return password.length >= 12 && kinds >= 3 ? "strong" : "medium";
}

export const STRENGTH_LABEL: Record<Strength, string> = { weak: "Weak", medium: "Medium", strong: "Strong" };
